"use client";

import { useState, useEffect } from "react";
import { CheckIcon, XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { CustomFormItemProps } from "../accelerators-form";
import { FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";

type RateOperator = {
  value: string;
  label: string;
  symbol: string;
};

const rateOperators: RateOperator[] = [
  {
    value: "eq",
    label: "is equal to",
    symbol: "=",
  },
  {
    value: "gt",
    label: "is greater than",
    symbol: ">",
  },
  {
    value: "gte",
    label: "is greater than or equal to",
    symbol: "≥",
  },
  {
    value: "lt",
    label: "is less than",
    symbol: "<",
  },
  {
    value: "lte",
    label: "is less than or equal to",
    symbol: "≤",
  },
  {
    value: "range",
    label: "is in range",
    symbol: "",
  },
];

type ConditionsProps = CustomFormItemProps & {
  onRemove?: () => void;
  showLabels?: boolean;
};

export function RateCombobox({
  value,
  onChange,
  onRemove,
  showLabels = false,
}: ConditionsProps) {
  const [open, setOpen] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState<string | undefined>(value.operator);
  const [rate, setRate] = useState<string>(value.rate);
  const [attainment, setAttainment] = useState<string>(value.attainment);
  const [attainmentFrom, setAttainmentFrom] = useState<string>(value.attainment_range_from);
  const [attainmentTo, setAttainmentTo] = useState<string>(value.attainment_range_to);

  const handleSelectOperator = (v: string) => {
    setSelectedOperator(v);
    setOpen(false);
  };

  useEffect(() => {
    onChange({
      operator: selectedOperator,
      attainment,
      attainment_range_from: attainmentFrom,
      attainment_range_to: attainmentTo,
      rate: rate,
    });
  }, [selectedOperator, rate, attainment, attainmentFrom, attainmentTo])

  return (
    <div className="flex flex-row items-start">

      {/* Attainment */}
      <div className="flex flex-col gap-y-2 w-[500px]">
        {showLabels && <FormLabel>Attainment</FormLabel>}
        <div className="flex flex-row gap-x-2 items-start">
          {/* Operator */}
          <div className="flex items-center space-x-4">
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-[220px] justify-start"
                >
                  {selectedOperator ? (
                    <>{rateOperators.find((c) => c.value === selectedOperator)?.label}</>
                  ) : (
                    <>Set condition</>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0" side="right" align="start">
                <Command>
                  <CommandInput placeholder="Search conditions..." />
                  <CommandList>
                    <CommandEmpty>No results found.</CommandEmpty>
                    <CommandGroup>
                      {rateOperators.map((operator) => (
                        <CommandItem
                          key={operator.value}
                          value={operator.value}
                          onSelect={(value) => handleSelectOperator(value)}
                        >
                          <span>{operator.label}</span>
                          <CheckIcon
                            className={cn(
                              "ml-auto h-4 w-4",
                              operator.value === selectedOperator
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                        </CommandItem>
                      )
                      )}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex flex-row items-start gap-x-1">
            {selectedOperator === "range" ? (
              <>
                <Input
                  value={attainmentFrom}
                  onChange={e => setAttainmentFrom(e.target.value)}
                  className="h-9 w-[100px]"
                  leftSection="≥"
                  rightSection="%"
                />
                <Input
                  value={attainmentTo}
                  onChange={e => setAttainmentTo(e.target.value)}
                  className="h-9 w-[100px]"
                  leftSection="<"
                  rightSection="%"
                />
              </>
            ) : (
              <Input
                value={attainment}
                onChange={e => setAttainment(e.target.value)}
                className="h-9 w-[100px]"
                leftSection={rateOperators.find(el => selectedOperator === el.value)?.symbol}
                rightSection="%"
              />
            )}
          </div>
        </div>
      </div>

      {/* Rate */}
      <div className="flex flex-col gap-y-2">
        {showLabels && <FormLabel>Commission</FormLabel>}
        <div className="flex flex-row items-center gap-x-3">
          <Input
            value={rate}
            onChange={e => setRate(e.target.value)}
            className="h-9 w-[100px]"
            rightSection="%"
          />
          {onRemove && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onRemove}
            >
              <XIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
